/* Bookstore landing — Scriptorium theme, composed from shared
 * primitives in `../components/landing/scriptorium/`.
 *
 * All original `bs-*` data-testids are preserved.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';   // Feb-2026 · i18n Wave 4
import { toast } from 'sonner';
import {
  ArrowRight, ArrowLeft, Sparkles, Store, PenTool,
  ShieldCheck, Lightbulb, Loader2, LogOut, UserCircle2,
} from 'lucide-react';
import { useSeoMeta } from '@/lib/useSeoMeta';
import { useHreflang } from '@/lib/useHreflang';   // 2026-02 · hreflang for all 6 locales
import { bookstoreApi, resolveCoverUrl } from '@/lib/bookstoreApi';
import { useBookstoreAuth } from '@/lib/bookstoreAuthContext';
import PublicNavBar from '@/components/landing/PublicNavBar';
import SpinCarousel from '@/components/bookstore/SpinCarousel';
import BookDetailModal from '@/components/bookstore/BookDetailModal';
import StacksSearchBar from '@/components/bookstore/StacksSearchBar';
import PoweredByEmergent from '@/components/landing/PoweredByEmergent';
import BookstoreThumbnailsStrip from '@/components/bookstore/BookstoreThumbnailsStrip';
import CurrencySelector from '@/components/CurrencySelector';
import LanguageCapsule  from '@/components/LanguageCapsule';   // Feb-2026 · i18n
import GenreContentWarningDialog, { hasAcceptedContentWarning } from '@/components/bookstore/GenreContentWarningDialog';
import AdultsOnlyGate, { hasAcceptedAdultsOnly } from '@/components/bookstore/AdultsOnlyGate';
import useHideMatureShelves from '@/lib/useHideMatureShelves';   // Feb-2026 · MA super-tile toggle
import PwaInstallCard from '@/components/PwaInstallCard';
import BookCard from '@/components/bookstore/BookCard';
import StructuredData, { bookstoreCollectionLd, breadcrumbLd } from '@/components/seo/StructuredData';
import { BOOKSTORE_READING_LANGUAGES } from '@/pages/bookstore/GenrePage';
import HeatPreferenceDialog, { shouldShowHeatPrompt } from '@/components/bookstore/HeatPreferenceDialog';
import TopTropesRail from '@/components/bookstore/TopTropesRail';
import { primaryLanguageCode } from '@/lib/languageFlags';

import { P, FONTS, GRAIN_URL, useScriptoriumFonts } from '@/components/landing/scriptorium/palette';


import WritersSalonModal  from '@/components/landing/scriptorium/WritersSalonModal';
import useBusinessUnread  from '@/lib/useBusinessUnread';
// iter-greyrom-followup-2 — Cross-shelf reader-warning preferences.
import useReaderTagPreferences from '@/lib/useReaderTagPreferences';
import { runTour } from '@/lib/tours';
import { useAuth } from '@/lib/auth_context';

import { landingApi } from '@/lib/landingApi';                            // Feb-2026 · marquee state
import { SpotlightsRow, NewArrivalsRow } from '@/components/bookstore/MerchRows'; // Feb-2026 · merch rails
// Sanity: keep BookstoreLanding on the same map (import kept named).

const SEO_FALLBACK = {
  title:       'Indie Fiction Ebook Store — Crown & Rose',
  description: 'Your Indie writing business, your way. Discover indie novels by writers who actually finished. Sample chapters, buy direct from working authors — authors keep 85% of every direct sale, or 90% of retailer net on Amazon KDP.',
  keywords:    'indie fiction ebook store, buy indie novels direct, read indie authors online, dark romance ebooks, mafia romance books, contemporary romance ebooks, author royalties 85 percent direct, author royalties 90 percent amazon kdp, publish indie ebook, direct-to-reader bookstore, DRM-free ebooks, support indie authors, your indie writing business',
  og_title:       'Crown & Rose · The Vault',
  og_description: 'Your Indie writing business, your way. A dark-academia bookshop for finished novels — sample, buy, subscribe.',
  og_image: 'https://static.prod-images.emergentagent.com/jobs/67d45115-9fac-4a11-82ed-b7a8bff08f0b/images/16fb06093028199bd17b2db4d3b1cc885cf86178d18e0da6f478fbce1cec0547.jpeg',
};

function GenreTile({ genre, onClick }) {
  const url = resolveCoverUrl(genre.image_url);
  const locked = !!genre.locked;
  const PALETTES = {
    fiction:                     `linear-gradient(135deg,${P.gold},${P.goldDeep})`,
    'sci-fi':                    `linear-gradient(135deg,#1d2c45,${P.leather})`,
    romance:                     `linear-gradient(135deg,#8b3a4a,${P.oxblood})`,
    // iter55p8 — Dark Romance shelves get a deeper, more saturated
    // oxblood-black gradient so the tile telegraphs the darker
    // content on the shelf before the reader even reads the label.
    'dark-romance':              `linear-gradient(135deg,#3d0910,#0d0405)`,
    // iter-greyrom — Grey Romance = the blended contemporary + dark
    // shelf. Legacy slug `dark-contemporary-romance` kept as a fallback
    // so any un-migrated tile still renders on-brand.
    'grey-romance':              `linear-gradient(135deg,#1c1a34,${P.oxblood})`,
    // Feb-2026 · Adults-Only super-tile — deep crimson-black to signal 18+.
    'adults-only':               `linear-gradient(135deg,#4a0511,#0a0202)`,
    // Feb-2026 · LGBTQIA+ tile — soft rainbow-tinted parchment gradient.
    'lgbtqia':                   `linear-gradient(135deg,#7a3f8e,#c95d6a 45%,#e8a55d)`,
    'our-community-is-diverse':  `linear-gradient(135deg,#194f46,#bd8b3c 52%,#7a3f8e)`,
    'dark-contemporary-romance': `linear-gradient(135deg,#1c1a34,${P.oxblood})`,
    mystery:                     `linear-gradient(135deg,${P.verdant},#0a1316)`,
    fantasy:                     `linear-gradient(135deg,#3b3157,#1a1428)`,
    nonfiction:                  `linear-gradient(135deg,${P.paperDeep},#7a7c79)`,
  };
  return (
    <button
      type="button"
      onClick={locked ? undefined : onClick}
      disabled={locked}
      data-testid={`genre-tile-${genre.slug}`}
      title={locked ? 'Coming soon — no titles available yet.' : ''}
      className={`group relative inline-flex aspect-[11/17] w-[8.75rem] shrink-0 overflow-hidden rounded-md border text-left transition sm:w-40 ${locked ? 'cursor-not-allowed opacity-60 grayscale' : 'hover:-translate-y-1 hover:shadow-lg'}`}
      style={{
        backgroundImage: PALETTES[genre.slug] || PALETTES.fiction,
        borderColor:     P.gold,
        boxShadow:       `0 8px 22px -12px ${P.leather}88`,
      }}
    >
      {url && (
        <img src={url} alt={genre.name}
             className={`absolute inset-0 h-full w-full object-cover transition ${locked ? 'opacity-70' : 'opacity-85 group-hover:opacity-100'}`} />
      )}
      <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${P.leather}dd 0%, ${P.leather}55 55%, transparent 90%)` }} />
      {locked && (
        <div
          data-testid={`genre-tile-locked-${genre.slug}`}
          className="absolute right-2 top-2 z-20 rounded-sm px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider shadow"
          style={{ backgroundColor: `${P.paperDeep}f0`, color: P.oxblood }}
        >
          Coming soon
        </div>
      )}
      <div className="relative z-10 flex h-full w-full flex-col justify-end p-3 sm:p-5" style={{ color: P.paper }}>
        <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: P.gold, fontFamily: FONTS.type }}>Shelf</div>
        <div className="mt-1 text-xl leading-tight" style={{ fontFamily: FONTS.display, fontWeight: 600 }}>{genre.name}</div>
        <p className="mt-1 text-[11.5px] leading-snug line-clamp-2" style={{ color: `${P.paper}dd`, fontFamily: FONTS.body }}>{genre.blurb}</p>
        <span
          className="mt-3 inline-flex w-fit items-center gap-1 rounded-sm px-2 py-0.5 text-[10.5px] font-semibold backdrop-blur"
          style={{ backgroundColor: '#E57A1C', color: '#fff' }}
        >
          {locked ? 'No titles yet' : <>Explore <ArrowRight className="h-3 w-3" /></>}
        </span>
      </div>
    </button>
  );
}

function SectionEyebrow({ children }) {
  return (
    <div className="text-[10.5px] uppercase tracking-[0.32em]" style={{ color: P.oxblood, fontFamily: FONTS.type }}>
      {children}
    </div>
  );
}
function SectionTitle({ children }) {
  return (
    <h2 className="text-2xl tracking-tight" style={{ fontFamily: FONTS.display, fontWeight: 600, color: P.ink }}>
      {children}
    </h2>
  );
}


const SUPPORTED_LOCALES = new Set(['fr', 'de', 'es', 'pt', 'zh']);
const SUB_NAV_LINKS = [
  { label: 'Home',        path: '/' },
  { label: 'Blog',        path: '/blog',                testId: 'bs-subnav-blog' },
  { label: 'How We Help', path: '/how-we-help-writers', testId: 'bs-subnav-how-we-help' },
  { label: 'FAQ',         path: '/faq',                 testId: 'bs-subnav-faq' },
];

/** Thin tab strip that connects the Bookstore into the site-wide nav network.
 *  Locale-aware: /fr/bookstore → links become /fr/blog, /fr/faq etc. */
function BookstoreSubNav() {
  const location = useLocation();
  const localeMatch = location.pathname.match(/^\/([a-z]{2})(\/|$)/);
  const pfx = localeMatch && SUPPORTED_LOCALES.has(localeMatch[1]) ? `/${localeMatch[1]}` : '';

  return (
    <div
      className="flex items-center gap-1 overflow-x-auto border-t px-4 py-1.5 sm:px-6"
      style={{ borderColor: `${P.gold}44`, backgroundColor: `${P.paper}cc` }}
      data-testid="bs-subnav"
    >
      {SUB_NAV_LINKS.map(({ label, path, testId }) => (
        <Link
          key={path}
          to={pfx + path}
          data-testid={testId}
          className="whitespace-nowrap rounded-md px-2.5 py-1 text-[11.5px] font-medium no-underline transition-colors hover:opacity-90"
          style={{ color: P.inkSoft, fontFamily: FONTS.type }}
        >
          {label}
        </Link>
      ))}
    </div>
  );
}


export default function BookstoreLanding() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2);
  useSeoMeta('/bookstore', SEO_FALLBACK);
  useHreflang('bookstore');   // injects hreflang for /bookstore, /fr/bookstore, /de/bookstore, etc.
  useScriptoriumFonts();
  const navigate = useNavigate();
  const { user, logout } = useBookstoreAuth();
  const { user: loomUser } = useAuth();
  const [data,    setData]    = useState(null);
  const [openBook,setOpenBook]= useState(null);
  const [email,   setEmail]   = useState('');
  const [joining, setJoining] = useState(false);
  const [salonOpen, setSalonOpen] = useState(false);
  // iter55p8 — Content-warning gate for the Dark Romance shelves.
  const [pendingWarnGenre, setPendingWarnGenre] = useState(null);
  // Feb-2026 — 5-checkbox age gate for the Adults-Only super-shelf.
  const [adultsOnlyGateOpen, setAdultsOnlyGateOpen] = useState(false);
  const [adultsOnlyChecklist, setAdultsOnlyChecklist] = useState(null);
  // Feb-2026 · Hide the Mature-Audiences super-tile when the reader
  // has toggled it off in their bookstore account.
  const { hidden: matureHidden } = useHideMatureShelves();

  // Feb-2026 (indie-conv batch2 pt3) — First-login heat-preference prompt.
  const [heatPrompt, setHeatPrompt] = useState(false);
  useEffect(() => {
    if (shouldShowHeatPrompt(user)) {
      const t = setTimeout(() => setHeatPrompt(true), 700);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [user]);

  // iter-p3-j — Accept-Language nudge banner on the landing page so
  // pre-genre-page traffic gets an early hint.  Runs once per browser
  // (dismissal persists to localStorage). Suppressed for readers who
  // already picked a preferred_language on their account, and for
  // English browsers.
  const [langNudge, setLangNudge] = useState(() => {
    try {
      if (typeof window === 'undefined') return null;
      if (localStorage.getItem('bs-lang-nudge-dismissed') === '1') return null;
      if (user?.preferred_language) return null;
      const nav = navigator.language || (navigator.languages || [])[0] || 'en';
      const primary = String(nav).split('-')[0].toLowerCase();
      if (primary === 'en') return null;
      const meta = BOOKSTORE_READING_LANGUAGES.find((l) => l.code === primary);
      return meta && meta.code ? meta : null;
    } catch { return null; }
  });
  const dismissLangNudge = () => {
    setLangNudge(null);
    try { localStorage.setItem('bs-lang-nudge-dismissed', '1'); } catch (_) { /* noop */ }
  };

  // iter-greyrom-followup-2 — Cross-shelf reader tag preferences. Any
  // tag the reader has excluded on any genre page is honoured on this
  // "For You" landing too, so mafia-averse readers stop seeing mafia
  // covers site-wide. `applyTo` returns the filtered array.
  const readerPrefs = useReaderTagPreferences();

  // iter-romance25 — Fire the For-You explainer tour once for readers
  // who now have a hidden-tag preference active on the landing.
  useEffect(() => {
    if (readerPrefs.hasPreferences) runTour('bookstore-for-you-open');
  }, [readerPrefs.hasPreferences]);

  const { unread: businessUnread } = useBusinessUnread({ enabled: Boolean(loomUser) });

  // Feb-2026 — Grand Opening marquee state.
  const [landingDoc, setLandingDoc] = useState(null);
  useEffect(() => {
    let alive = true;
    landingApi.get()
      .then((d) => { if (alive) setLandingDoc(d); })
      .catch(() => { /* banner just hides if unreachable */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    bookstoreApi.catalog()
      .then(setData)
      .catch(() => toast.error('Failed to load the catalog.'));
  }, []);

  useEffect(() => {
    if (!data) return;
    const hash = window.location.hash || '';
    const match = hash.match(/^#book=([^&]+)/);
    if (!match) return;
    const bookId = decodeURIComponent(match[1]);
    bookstoreApi.book(bookId)
      .then((r) => {
        if (r?.book) setOpenBook(r.book);
        else toast.info('That title is no longer available.');
      })
      .catch(() => toast.info('Could not load that title.'))
      .finally(() => {
        try { window.history.replaceState({}, '', '/bookstore'); } catch (_e) { /* noop */ }
      });
  }, [data]);

  const onWaitlist = async (e) => {
    e.preventDefault();
    if (!email) return;
    setJoining(true);
    try {
      await bookstoreApi.joinWaitlist(email);
      toast.success('You\'re on the list — we\'ll be in touch.');
      setEmail('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not join the waitlist.');
    } finally {
      setJoining(false);
    }
  };

  const startMaven = async () => {
    if (!user) {
      toast.info('Sign in to your Book Store account to become a Weekly Maven.');
      navigate('/bookstore/login?next=/bookstore');
      return;
    }
    try {
      const { url } = await bookstoreApi.subscribeMaven(window.location.origin, user.preferred_genre);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start Maven checkout.');
    }
  };

  const m = data?.marketing || {};

  const openLanguageShelf = (book, code) => {
    const language = primaryLanguageCode(code);
    const genre = (data?.genres || []).find((item) => (
      item.id === book.genre_id || item.slug === book.genre_slug
    ));
    const genreSlug = genre?.slug || book.genre_slug;
    if (!language || !genreSlug) return;
    try { localStorage.setItem(`bs-shelf-language:${genreSlug}`, language); } catch { /* noop */ }
    navigate(`/bookstore/g/${genreSlug}`);
  };

  // Override CSS tokens on the bookstore root so shared bookstore
  // components (BookCard, SpinCarousel, ThumbnailsStrip) inherit the
  // Scriptorium palette without needing bespoke props.
  const cssVars = {
    '--background':          '38 30% 95%',
    '--foreground':          '30 15% 15%',
    '--card':                '38 30% 92%',
    '--card-foreground':     '30 15% 15%',
    '--border':              '38 30% 78%',
    '--primary':             '0 40% 30%',
    '--primary-foreground':  '38 30% 95%',
    '--muted-foreground':    '30 12% 40%',
    '--secondary':           '38 25% 88%',
    '--panel-header':        '38 30% 92%',
  };

  return (
    <div
      className="relative min-h-screen pb-[max(6rem,env(safe-area-inset-bottom))]"
      style={{
        ...cssVars,
        backgroundColor: P.paper,
        color:           P.ink,
        fontFamily:      FONTS.body,
      }}
      data-testid="bookstore-landing"
    >
      {/* iter55g69 — CollectionPage schema so /bookstore can rank
       *  for storefront-intent searches ("indie ebook store", etc). */}
      <StructuredData
        id="bookstore-collection"
        data={bookstoreCollectionLd({
          siteUrl: typeof window !== 'undefined' ? window.location.origin : '',
          lang,
        })}
      />
      <StructuredData
        id="breadcrumb"
        data={breadcrumbLd([
          { name: 'Crown & Rose', url: typeof window !== 'undefined' ? window.location.origin : '' },
          { name: 'Book Store', url: typeof window !== 'undefined' ? `${window.location.origin}/bookstore` : '' },
        ])}
      />
      <div className="pointer-events-none fixed inset-0 z-0 opacity-25" style={{ backgroundImage: GRAIN_URL }} />
      <div className="relative">
        {/* iter-p3-j — Accept-Language nudge banner. Dismiss persists in localStorage. */}
        {langNudge && (
          <div
            className="relative z-10 flex items-center justify-center gap-3 border-b border-amber-500/40 bg-amber-100/80 px-4 py-2 text-[12.5px] text-amber-950"
            data-testid="bookstore-lang-nudge"
            dir={langNudge.rtl ? 'rtl' : undefined}
          >
            <span>
              <b>See titles in {langNudge.label} →</b>
              <span className="ml-2 opacity-80">Your browser is set to this language — filter the store to match with one tap.</span>
            </span>
            <button
              type="button"
              onClick={() => {
                // iter-p3-k — Personalise the nudge deep-link.  When
                // the reader has a preferred genre on their account
                // (or a bookstore session hint) we deep-link them to
                // that shelf with the language pre-selected — one
                // tap should feel personal, not generic.
                const targetGenre =
                  (user?.preferred_genre || '').trim().toLowerCase() ||
                  (() => {
                    try {
                      return (localStorage.getItem('bs-last-genre') || '')
                        .trim().toLowerCase();
                    } catch { return ''; }
                  })() ||
                  'romance';
                try { localStorage.setItem(`bs-shelf-language:${targetGenre}`, langNudge.code); } catch (_) { /* noop */ }
                navigate(`/bookstore/g/${targetGenre}`);
              }}
              data-testid="bookstore-lang-nudge-apply"
              className="rounded-sm bg-amber-600 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-white hover:bg-amber-700"
            >
              Browse in {langNudge.label}
            </button>
            <button
              type="button"
              onClick={dismissLangNudge}
              data-testid="bookstore-lang-nudge-dismiss"
              className="rounded-sm border border-amber-700/40 px-1.5 py-0.5 text-[11px] hover:bg-amber-200"
              aria-label="Dismiss language nudge"
            >
              ×
            </button>
          </div>
        )}

      </div>


      {/* Top nav — shared PublicNavBar */}
      <PublicNavBar
        onSignIn={() => navigate('/bookstore/login')}
        onSignUp={() => navigate('/bookstore/login')}
      />
      {/* Bookstore reader account pill — only shown when signed into a bookstore account */}
      {user && (
        <div className="flex items-center justify-end gap-3 border-b px-4 py-1.5 text-[11.5px] sm:px-6" style={{ borderColor: `${P.gold}44`, backgroundColor: `${P.paper}cc` }}>
          <CurrencySelector />
          <Link to="/bookstore/account" data-testid="bs-nav-account" className="inline-flex items-center gap-1.5 font-medium hover:opacity-80" style={{ color: P.oxblood, fontFamily: FONTS.display }}>
            <UserCircle2 className="h-3.5 w-3.5" /> {user.name}
          </Link>
          <button onClick={logout} data-testid="bs-nav-logout" className="inline-flex items-center gap-1 opacity-60 hover:opacity-100" style={{ color: P.inkSoft }}>
            <LogOut className="h-3 w-3" /> Sign out
          </button>
          <LanguageCapsule variant="landing" />
        </div>
      )}

      {/* Search bar — moved above hero for maximum discovery prominence (Feb-2026) */}
      <StacksSearchBar onOpenBook={setOpenBook} />

      {/* Hero */}
      <section className="relative z-10 mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-12 lg:py-16 lg:pl-24">
        <div
          className="mb-5 hidden items-center gap-2 rounded-full border px-3 py-1 text-[10.5px] uppercase tracking-[0.28em] sm:inline-flex"
          style={{ borderColor: P.gold, backgroundColor: P.paperDeep, color: P.oxblood, fontFamily: FONTS.type }}
        >
          <Store className="h-3 w-3" /> Now open
        </div>
        <h1
          className="max-w-3xl break-words text-4xl leading-tight sm:text-5xl lg:text-6xl"
          style={{ fontFamily: FONTS.display, fontWeight: 500, color: P.ink }}
          data-testid="bs-hero-headline"
        >
          {m.hero_headline || 'Welcome to our bookstore.'}
        </h1>
        <p className="mt-4 hidden max-w-2xl break-words text-lg sm:block" style={{ color: P.inkSoft }} data-testid="bs-hero-subline">
          {m.hero_subline || 'Browse genres, sample chapters, and meet the next book that won\'t leave you alone.'}
        </p>
      </section>

      <div className="relative z-10 lg:pl-16">
        <BookstoreThumbnailsStrip />
      </div>

      {/* PWA install card — kit placement: "top of bookstore; hide once dismissed" */}
      <div className="mx-auto max-w-3xl px-4 py-4 sm:px-6" data-testid="bookstore-pwa-card-slot">
        <PwaInstallCard />
      </div>


      {/* iter-p3-r — AI Books top-level chip. Kept as a quick secondary
       *  entry point beneath the search bar; the prominent CTA above
       *  is the primary discovery surface. */}
      {data && (
        <div
          className="mx-auto mt-3 flex max-w-6xl flex-wrap items-center gap-2 px-4 sm:px-6"
          data-testid="bs-top-chips"
        >
          <Link
            to="/bookstore/ai-books"
            data-testid="bs-ai-books-chip"
            className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11.5px] font-semibold transition hover:shadow-panel-active"
            style={{ borderColor: `${P.gold}88`, color: P.ink, backgroundColor: P.paperDeep }}
          >
            <Sparkles className="h-3.5 w-3.5" />
            AI Books
            {typeof data?.ai_book_count === 'number' && data.ai_book_count > 0 && (
              <span className="rounded-full px-1.5 py-0.5 text-[9.5px] font-bold" style={{ backgroundColor: P.oxblood, color: P.paper }}>
                {data.ai_book_count}
              </span>
            )}
          </Link>
          <Link
            to="/faq"
            data-testid="bs-faq-chip"
            className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11.5px] font-semibold hover:shadow-panel-active"
            style={{ borderColor: `${P.gold}66`, color: P.inkSoft, backgroundColor: P.paper }}
          >
            <Lightbulb className="h-3.5 w-3.5" />
            FAQ
          </Link>
        </div>
      )}

      {/* iter55p8 — Content warning modal, mounted at the top so any
       * genre-tile click can raise it. */}
      <GenreContentWarningDialog
        genre={pendingWarnGenre}
        onAcceptOnce={(g) => { setPendingWarnGenre(null); navigate(`/bookstore/g/${g.slug}`); }}
        onRetreat={() => setPendingWarnGenre(null)}
      />

      {/* Feb-2026 — Adults-Only 5-checkbox age gate. */}
      <AdultsOnlyGate
        open={adultsOnlyGateOpen}
        checklist={adultsOnlyChecklist}
        onAccept={() => { setAdultsOnlyGateOpen(false); navigate('/bookstore/g/adults-only'); }}
        onRetreat={() => setAdultsOnlyGateOpen(false)}
      />

      {/* Genres — bookmark reverted to the page-top in iter-56p per
       * user request; genre carousel no longer wraps it. */}
      <section className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:pl-24" data-testid="bs-genre-section">
        <div className="mb-3">
          <SectionEyebrow>Pick a shelf</SectionEyebrow>
          <SectionTitle>Browse by genre</SectionTitle>
        </div>
        {!data ? (
          <div className="flex h-72 items-center justify-center" style={{ color: P.inkSoft }}>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading the shelves…
          </div>
        ) : (
          <>
            <SpinCarousel testid="bs-genre-carousel">
              {data.genres
                // Feb-2026 — Hide any child of the Adults-Only super-shelf,
                // AND the super-tile itself, from the scrolling carousel —
                // it's pinned separately below so it's never buried behind
                // a "next" click (iter-ma-visibility fix: the tile used to
                // sit at sort_order 100, last of 8, invisible without
                // scrolling the carousel twice).
                .filter((g) => !g.hide_from_main_shelves && g.parent_group !== 'adults-only' && g.slug !== 'adults-only')
                .map((g) => (
                  <GenreTile
                    key={g.id}
                    genre={g}
                    onClick={() => {
                      bookstoreApi.trackStorefrontEvent({ event_type: 'genre_click', genre_slug: g.slug });
                      // iter55p8 — Any shelf tagged content_warning gates
                      // through the modal on first visit. Acceptance is
                      // remembered per-slug via localStorage.
                      if (g.content_warning && !hasAcceptedContentWarning(g.slug)) {
                        setPendingWarnGenre(g);
                      } else {
                        navigate(`/bookstore/g/${g.slug}`);
                      }
                    }}
                  />
                ))}
            </SpinCarousel>

            {/* iter-ma-visibility — Mature Audiences Only pinned, always
             * on-screen (never inside the scrolling carousel), so the
             * 18+ gate is actually discoverable on first load instead of
             * requiring two "next" clicks to even see it exists. Reader
             * can still permanently hide it from /bookstore/account. */}
            {!matureHidden && (() => {
              const adultsOnlyGenre = data.genres.find((g) => g.slug === 'adults-only');
              if (!adultsOnlyGenre) return null;
              return (
                <div className="mt-4 max-w-xs" data-testid="bs-mature-pinned-tile">
                  <GenreTile
                    genre={adultsOnlyGenre}
                    onClick={() => {
                      bookstoreApi.trackStorefrontEvent({ event_type: 'genre_click', genre_slug: adultsOnlyGenre.slug });
                      if (hasAcceptedAdultsOnly()) {
                        navigate(`/bookstore/g/${adultsOnlyGenre.slug}`);
                      } else {
                        setAdultsOnlyChecklist(adultsOnlyGenre.gate_checklist || null);
                        setAdultsOnlyGateOpen(true);
                      }
                    }}
                  />
                </div>
              );
            })()}
          </>
        )}
      </section>

      {/* iter-greyrom-followup-2 — Cross-shelf reader tag preferences. */}
      {readerPrefs.hasPreferences && (
        <section className="relative z-10 mx-auto max-w-6xl px-6 pt-6 lg:pl-24" data-testid="bs-for-you-callout">
          <div
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3"
            style={{ borderColor: `${P.oxblood}55`, backgroundColor: `${P.oxblood}0c`, color: P.ink }}
          >
            <div className="flex items-center gap-2 text-[12px]">
              <Sparkles className="h-3.5 w-3.5" style={{ color: P.oxblood }} />
              <span>
                <span className="font-semibold">Personalised for you.</span>{' '}
                We&rsquo;re hiding titles tagged with <em>{[...readerPrefs.excluded].slice(0, 4).join(', ')}
                {readerPrefs.excluded.size > 4 ? `, +${readerPrefs.excluded.size - 4} more` : ''}</em>
                {' '}across every shelf.{' '}
                <Link
                  to="/bookstore/account#warnings"
                  data-testid="bs-for-you-manage-link"
                  className="underline hover:opacity-80"
                  style={{ color: P.oxblood }}
                >
                  Manage warnings →
                </Link>
              </span>
            </div>
            <button
              onClick={readerPrefs.clearAll}
              data-testid="bs-for-you-clear"
              className="rounded-sm border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider hover:opacity-80"
              style={{ borderColor: P.oxblood, color: P.oxblood, backgroundColor: 'transparent' }}
            >
              Show everything
            </button>
          </div>
        </section>
      )}

      {/* Spotlight */}
      {readerPrefs.applyTo(data?.spotlights)?.length > 0 && (
        <section className="relative z-10 mx-auto max-w-6xl px-6 pt-8 lg:pl-24" data-testid="bs-spotlight-section">
          <div className="mb-3">
            <SectionEyebrow><Sparkles className="mr-1 inline h-3 w-3" /> Spotlight</SectionEyebrow>
            <SectionTitle>Books of the month</SectionTitle>
          </div>
          <SpinCarousel testid="bs-spotlight-carousel">
            {readerPrefs.applyTo(data.spotlights).map((b) => (<BookCard key={b.id} book={b} onClick={setOpenBook} onLanguageClick={(code) => openLanguageShelf(b, code)} />))}
          </SpinCarousel>
        </section>
      )}

      {/* Feb-2026 (indie-conv batch2 pt3) — Trending tropes rail. */}
      <TopTropesRail />

      {/* Best-sellers */}
      {readerPrefs.applyTo(data?.bestsellers)?.length > 0 && (
        <section className="relative z-10 mx-auto max-w-6xl px-6 pt-8 lg:pl-24" data-testid="bs-bestseller-section">
          <div className="mb-3">
            <SectionEyebrow>Best-sellers</SectionEyebrow>
            <SectionTitle>By authors you&rsquo;ll love</SectionTitle>
          </div>
          <SpinCarousel testid="bs-bestseller-carousel">
            {readerPrefs.applyTo(data.bestsellers).map((b) => (<BookCard key={b.id} book={b} onClick={setOpenBook} onLanguageClick={(code) => openLanguageShelf(b, code)} />))}
          </SpinCarousel>
        </section>
      )}

      {/* Feb-2026 — New Arrivals + Genre Spotlights above the fold. */}
      <NewArrivalsRow />
      <SpotlightsRow />

      {/* Upcoming */}
      {readerPrefs.applyTo(data?.upcoming)?.length > 0 && (
        <section className="relative z-10 mx-auto max-w-6xl px-6 pt-8 lg:pl-24" data-testid="bs-upcoming-section">
          <div className="mb-3">
            <SectionEyebrow>Up-and-coming</SectionEyebrow>
            <SectionTitle>Talent you will love</SectionTitle>
          </div>
          <SpinCarousel testid="bs-upcoming-carousel">
            {readerPrefs.applyTo(data.upcoming).map((b) => (<BookCard key={b.id} book={b} onClick={setOpenBook} onLanguageClick={(code) => openLanguageShelf(b, code)} />))}
          </SpinCarousel>
        </section>
      )}

      {/* Weekly Maven CTA */}
      <section className="relative z-10 mx-auto max-w-6xl px-6 pt-12 lg:pl-24" data-testid="bs-maven-section">
        <div
          className="relative overflow-hidden rounded-lg border p-5 sm:p-8 lg:p-10"
          style={{
            background: `linear-gradient(140deg, ${P.paperDeep} 0%, ${P.paper} 60%, ${P.gold}22 100%)`,
            borderColor: P.gold,
            boxShadow:  `0 24px 60px -30px ${P.leather}66`,
          }}
        >
          <div className="pointer-events-none absolute inset-0 opacity-30" style={{ backgroundImage: GRAIN_URL }} />
          <div className="relative grid grid-cols-1 items-center gap-6 lg:grid-cols-[1fr_auto]">
            <div>
              <div
                className="mb-3 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10.5px] uppercase tracking-[0.28em]"
                style={{ borderColor: `${P.oxblood}55`, backgroundColor: `${P.oxblood}12`, color: P.oxblood, fontFamily: FONTS.type }}
              >
                <Sparkles className="h-3 w-3" /> Weekly delivery
              </div>
              <h3
                className="text-2xl tracking-tight sm:text-3xl"
                style={{ fontFamily: FONTS.display, fontWeight: 600, color: P.ink }}
              >
                {m.maven_headline || 'Become a Weekly Maven'}
              </h3>
              <p className="mt-2 max-w-xl text-[13.5px]" style={{ color: P.inkSoft }}>
                {m.maven_subline || 'A new title in your bookshelf every week — pick whatever\'s calling you.'}
              </p>
              <div className="mt-3 inline-flex items-baseline gap-1" style={{ color: P.ink }}>
                <span
                  className="text-3xl tracking-tight"
                  style={{ fontFamily: FONTS.display, fontWeight: 600 }}
                >
                  ${(m.maven_price_usd ?? 9.99).toFixed(2)}
                </span>
                <span className="text-[12px]" style={{ color: P.inkSoft }}>/ month</span>
              </div>
            </div>
            <button
              type="button"
              onClick={startMaven}
              data-testid="bs-maven-subscribe"
              className="inline-flex items-center justify-center gap-2 rounded-md px-5 py-3 text-[13px] font-semibold transition hover:brightness-110"
              style={{ backgroundColor: P.oxblood, color: P.paper, fontFamily: FONTS.display }}
            >
              <Sparkles className="h-4 w-4" /> Become a Weekly Maven
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* The Loom CTA + Waitlist */}
      <section className="relative z-10 mx-auto max-w-6xl px-6 py-10 lg:pl-24" data-testid="bs-prowriter-cta">
        <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2">
          <div className="rounded-xl border p-6" style={{ backgroundColor: P.paperDeep, borderColor: `${P.gold}aa` }}>
            <div className="mb-2 inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em]" style={{ color: P.oxblood, fontFamily: FONTS.type }}>
              <Lightbulb className="h-3 w-3" /> For aspiring authors
            </div>
            <h3 className="text-xl tracking-tight" style={{ fontFamily: FONTS.display, fontWeight: 600, color: P.ink }}>
              Do you have an idea for your own story?
            </h3>
            <p className="mt-2 text-[13px]" style={{ color: P.inkSoft }}>
              The Loom is the same studio our published authors used. Multi-model AI, chapter-aware context, version history, plagiarism scans, and one-click DOCX export.
            </p>
            <Link
              to="/app"
              data-testid="bs-cta-prowriter-suite"
              className="mt-4 inline-flex items-center gap-2 rounded-md px-4 py-2 text-[13px] font-semibold"
              style={{ backgroundColor: P.oxblood, color: P.paper, fontFamily: FONTS.display }}
            >
              <PenTool className="h-4 w-4" /> Open the The Loom Suite
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <form
            onSubmit={onWaitlist}
            data-testid="bs-waitlist-form"
            className="rounded-xl border p-6"
            style={{ backgroundColor: P.paperDeep, borderColor: `${P.gold}aa` }}
          >
            <div className="mb-2 inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em]" style={{ color: P.oxblood, fontFamily: FONTS.type }}>
              <Sparkles className="h-3 w-3" /> Stay in the loop
            </div>
            <h3 className="text-xl tracking-tight" style={{ fontFamily: FONTS.display, fontWeight: 600, color: P.ink }}>
              Hear about new arrivals first.
            </h3>
            <p className="mt-1 text-[13px]" style={{ color: P.inkSoft }}>
              No spam. We email when a spotlight book lands or a bundle goes live.
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourdomain.com"
                data-testid="bs-waitlist-email"
                className="flex-1 rounded-md border px-3 py-2 text-[13px] outline-none"
                style={{ backgroundColor: P.paper, borderColor: `${P.gold}aa`, color: P.ink }}
              />
              <button
                type="submit"
                disabled={joining}
                data-testid="bs-waitlist-submit"
                className="inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-[13px] font-semibold disabled:opacity-60"
                style={{ backgroundColor: P.oxblood, color: P.paper, fontFamily: FONTS.display }}
              >
                {joining ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Notify me
              </button>
            </div>
          </form>
        </div>
      </section>

      <footer className="relative z-10 border-t px-6 py-6" style={{ borderColor: `${P.gold}66`, backgroundColor: P.paperDeep }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between text-[11px]" style={{ color: P.inkSoft }}>
          <div>© Crown & Rose · All rights reserved.</div>
          <Link to="/bookstore/admin" data-testid="bs-staff-link" className="opacity-60 hover:opacity-100" style={{ color: P.inkSoft }}>
            Staff
          </Link>
        </div>
        <PoweredByEmergent testId="bookstore-landing-powered-by-emergent" />
      </footer>

      {openBook && <BookDetailModal book={openBook} onClose={() => setOpenBook(null)} />}
      {heatPrompt && (
        <HeatPreferenceDialog onDone={() => setHeatPrompt(false)} />
      )}
      <WritersSalonModal open={salonOpen} onClose={() => setSalonOpen(false)} />
    </div>
  );
}
